import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Github } from 'lucide-react';
import { heroFooterSocials, getSocialById } from '../../data/socialLinks';
import SocialIcon from '../common/SocialIcon';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-black/5 dark:border-white/10 bg-white/50 dark:bg-slate-950/50 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 md:items-start">
          <div className="space-y-4 md:justify-self-start">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-brand-amber transition-colors"
              aria-label="Retour en haut"
            >
              <span className="text-lg">↑</span>
              <span>  {t('home.Backtotop')}</span>
            </button>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-4 md:justify-self-center md:text-center">
            <h4 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-sm">Navigation</h4>
            <nav className="flex flex-col gap-2.5">
              <Link to="/art" className="text-sm text-slate-600 dark:text-slate-400 hover:text-brand-amber transition-colors">
                {t('nav.art')} Gallery
              </Link>
              <Link to="/stream" className="text-sm text-slate-600 dark:text-slate-400 hover:text-brand-purple transition-colors">
                {t('nav.stream')} Hub
              </Link>
              <Link to="/about" className="text-sm text-slate-600 dark:text-slate-400 hover:text-brand-indigo transition-colors">
                {t('nav.about')}
              </Link>
              <Link to="/contact" className="text-sm text-slate-600 dark:text-slate-400 hover:text-brand-indigo transition-colors">
                {t('nav.contact')}
              </Link>
            </nav>
          </div>

          {/* Socials Column */}
          <div className="space-y-4 md:justify-self-end md:text-right">
            <h4 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-sm">
              {t('home.socialsTitle')}
            </h4>
            <div className="flex flex-wrap gap-2 md:justify-end">
              {heroFooterSocials.map(id => {
                const social = getSocialById(id);
                return social ? <SocialIcon key={social.id} social={social} size="sm" /> : null;
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-black/5 dark:border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1.5 font-medium">
            <span>© {currentYear}</span>
            <span className="text-slate-300 dark:text-slate-700">•</span>
            <span>{t('common.madeWith')}</span>
            <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500 inline animate-pulse" />
            <span>{t('common.by')}</span>
            <Link to="/" className="text-slate-700 dark:text-slate-200 hover:text-brand-indigo font-semibold transition-colors">
              Kynothan
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
            <Link to="/legal" className="hover:text-slate-900 dark:hover:text-white transition-colors">{t('legal.nav.legal')}</Link>
            <Link to="/privacy" className="hover:text-slate-900 dark:hover:text-white transition-colors">{t('legal.nav.privacy')}</Link>
            <Link to="/cookies" className="hover:text-slate-900 dark:hover:text-white transition-colors">{t('legal.nav.cookies')}</Link>
            <a
              href="https://github.com/Kynothan"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white transition-colors sm:ml-2"
            >
              <Github className="w-4 h-4" />
              <span>GitHub</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
